import { watch } from 'vue'
import { type Composer, createI18n as create } from 'vue-i18n'

declare module 'vue' {
  interface App {
    getI18n: () => I18n
  }
}

const COOKIE = 'k2_locale'

const COOKIE_MAX_AGE = 365 * 24 * 60 * 60

export type LocaleMessages = Record<string, object | (() => Promise<{ default: object }>)>

// Structural, not the DOM lib: server consumers of this module have no DOM in `lib`.
const browser = globalThis as {
  document?: { documentElement: { lang: string }; cookie: string }
  location?: { protocol: string }
}

const primaryOf = (tag: string) => tag.trim().split('-')[0]?.toLowerCase() ?? ''

const first = (messages: LocaleMessages) => Object.keys(messages)[0] ?? ''

const match = (tag: string, keys: readonly string[]) => {
  if (keys.includes(tag)) return tag
  const primary = primaryOf(tag)
  return primary ? keys.find(key => primaryOf(key) === primary) : undefined
}

export const resolveLanguage = (tag: string | null | undefined, messages: LocaleMessages) =>
  tag ? match(tag.trim(), Object.keys(messages)) : undefined

export const languageCookie = (language: string, secure: boolean) =>
  `${COOKIE}=${language}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure ? '; Secure' : ''}`

const fromCookies = (cookies: string | null | undefined, messages: LocaleMessages) => {
  for (const entry of cookies?.split(';') ?? []) {
    const separator = entry.indexOf('=')
    if (separator < 0) continue
    if (entry.slice(0, separator).trim() !== COOKIE) continue
    return resolveLanguage(decodeURIComponent(entry.slice(separator + 1).trim()), messages)
  }
  return undefined
}

const fromAcceptLanguage = (header: string | null | undefined, messages: LocaleMessages) =>
  header
    ?.split(',')
    .map(part => {
      const [tag, ...parameters] = part.split(';').map(piece => piece.trim())
      const quality = parameters.find(parameter => parameter.startsWith('q='))
      return { tag, quality: quality ? Number(quality.slice(2)) : 1 }
    })
    .filter(entry => entry.tag && Number.isFinite(entry.quality) && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality)
    .reduce<string | undefined>((found, { tag }) => found ?? resolveLanguage(tag, messages), undefined)

export const languageFromRequest = (headers: Headers, messages: LocaleMessages) =>
  fromCookies(headers.get('cookie'), messages) ?? fromAcceptLanguage(headers.get('accept-language'), messages) ?? first(messages)

const load = async (composer: Composer, messages: LocaleMessages, language: string) => {
  if (Object.keys(composer.getLocaleMessage(language)).length) return
  const source = messages[language]
  if (!source) return
  composer.setLocaleMessage(language, typeof source === 'function' ? (await source()).default : source)
}

export const createI18n = async (messages: LocaleMessages, language?: string) => {
  const fallback = first(messages)
  const active = resolveLanguage(language, messages) ?? fallback
  const i18n = create({
    legacy: false,
    locale: active,
    fallbackLocale: fallback,
    messages: Object.fromEntries(Object.keys(messages).map(language => [language, {}]))
  })

  watch(i18n.global.locale, next => {
    if (browser.document) {
      browser.document.documentElement.lang = next
      browser.document.cookie = languageCookie(next, browser.location?.protocol === 'https:')
    }
    void load(i18n.global, messages, next)
  })

  const needed = active === fallback ? [active] : [active, fallback]
  await Promise.all(needed.map(language => load(i18n.global, messages, language)))

  return i18n
}

export type I18n = Awaited<ReturnType<typeof createI18n>>
